import React, { FC, Fragment, ReactNode, useState, useEffect } from 'react';
import { Box, Button, Grid, Typography } from '@material-ui/core';
import { Link, navigate, useParams } from '@reach/router';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import { PageTextContentType } from '../../hooks/useContentMgmt';
import colors from '../../themes/main-colors';
import ROUTES from '../../routes';
import SuccessIcon from '../installer-portal/static/success.svg';
import CloseIcon from '../installer-portal/static/close.svg';
import { JobStatus, QuoteStatus } from '../../__generated__/pgeplus-types';
import Alert from '../../components/alerts';
import {
  GetJobOutputEvType,
  useInstallerJobDetailsQuery,
} from '../../hooks/pge-plus/useInstallerJobDetailsQuery';
import theme from '../../themes/theme';
import PGEButton from '../buttons/PGE-Button';
import { usePrintOrDownload } from '../../hooks/usePrintOrDownload';
import {
  boldText,
  subText1,
} from '../installer-portal/installer-common/quoteCss';
import { formatDateString, getTimeRangeString } from '../../util/date-utils';

const fontRegular = {
  fontSize: '20px !important',
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px !important',
  },
};

const fontBold = {
  fontSize: '20px !important',
  fontWeight: 700,
  [theme.breakpoints.down('sm')]: {
    fontSize: '16px !important',
  },
};

interface Props {
  pageHeaderInfo: {
    pageTitle?: string;
    pageSubTitle?: string;
    reasonTitle?: string;
    downloadPackageTitle?: string;
  };
  cancelReasonInfo?: {
    reasonTitle?: string;
    statusReason?: string;
    downloadPackageTitle?: string;
    hideDownloadOption?: boolean;
  };
  pendingReasonInfo?: {
    jobStatus?: string;
    downloadPackageTitle?: string;
    successTitle?: string;
  };
  paymentStatus?: {
    jobStatus?: string;
  };
  content: PageTextContentType;
  jobData: GetJobOutputEvType;
  bannerText?: ReactNode;
  parentPage?: string;
}

const InstallerPageHeader: FC<Props> = ({
  pageHeaderInfo,
  cancelReasonInfo,
  pendingReasonInfo,
  content,
  jobData,
  bannerText,
  parentPage,
}: Props) => {
  const { pageTitle, pageSubTitle, downloadPackageTitle } = pageHeaderInfo;

  const [isAlertOpen, toggleAlertBox] = useState(true);
  const { jobId } = useParams();
  const { handleDownload } = usePrintOrDownload({
    containerId: 'print_section',
    excludeSelectors: ['[data-noprint]'],
  });

   return (
    <Fragment>
      <Grid container item xs={12}>
        <Grid
          item
          xs={12}
          css={{ marginBottom: 30, marginTop: 30 }}
          data-print-exclude
          data-print-exclude-requote
        >
          <span css={{ color: colors.sparkBlue }}>{'<'}</span>
          <Link
            to={ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_LISTING}
            css={{
              fontSize: '18px',
              fontWeight: 700,
            }}
          >
            {content.get('BACK_TO_JOBS_LIST')}
          </Link>
        </Grid>
     
        <Grid
          item
          xs={12}
          css={{ marginBottom: pageSubTitle ? 20 : 30 }}
          data-print-exclude-requote
        >
          <Typography variant={'h1'}>{pageTitle}</Typography>
        </Grid>

        {parentPage === 'job-cancelled' && (
          <>
            <Grid
              item
              xs={12}
              css={{
                marginBottom: theme.spacing(3),
                '.MuiAlertTitle-root': {
                  marginBottom: 0,
                },
              }}
              data-noprint
            >
              <Alert
                isOpen={isAlertOpen}
                severity={'info'}
                variant={'filled'}
                title={
                  jobData?.job?.status === JobStatus.Expired
                    ? content.get('LEAD_EXPIRED_BANNER_TEXT')
                    : content.get('JOB_CANCELLED_BANNER_TEXT')
                }
                style={{
                  backgroundColor: colors.lightGray4,
                }}
              />
            </Grid>
          </>
        )}
        {parentPage === 'view-quote' && (
          <>
            {jobData?.job?.status === JobStatus.Canceled &&
            jobData?.job?.quote?.status === QuoteStatus.Created ? (
              <>
                <Grid
                  item
                  xs={12}
                  css={{
                    marginBottom: theme.spacing(3),
                    '.MuiAlertTitle-root': {
                      marginBottom: 0,
                    },
                  }}
                  data-noprint
                >
                  <Alert
                    isOpen={isAlertOpen}
                    severity={'info'}
                    variant={'filled'}
                    title={content.get('JOB_CANCELLED_BANNER_TEXT')}
                    style={{
                      backgroundColor: colors.lightGray4,
                    }}
                  />
                </Grid>
              </>
            ) : jobData?.job?.quote?.status !== QuoteStatus.Rejected ? (
              <>
                <Grid
                  item
                  xs={12}
                  css={{
                    marginBottom: theme.spacing(3),
                    '.MuiAlertTitle-root': {
                      marginBottom: 0,
                    },
                  }}
                  data-noprint
                >
                  <Alert
                    isOpen={isAlertOpen}
                    severity={'success'}
                    variant={'filled'}
                    title={bannerText}
                    onClose={() => toggleAlertBox(!isAlertOpen)}
                  />
                </Grid>
                <Grid
                  item
                  container
                  xs={12}
                  direction="column"
                  css={{ margin: '10px 0 30px 0' }}
                >
                  <Typography>
                    {content.get('CUSTOMER_HAS_TO_ACCEPT_QUOTE_BY')}:
                  </Typography>
                  {jobData?.job?.quote?.expiresAt ? (
                    <Grid item>
                      <Typography
                        variant="body1"
                        css={{ ...subText1, ...boldText }}
                      >
                        {formatDateString(jobData?.job?.quote?.expiresAt)}
                      </Typography>
                    </Grid>
                  ) : (
                    <Typography
                      variant="body1"
                      css={{ ...subText1, ...boldText }}
                    >
                      {content.get('TO_BE_SCHEDULED')}
                    </Typography>
                  )}
                </Grid>
                <Grid
                  item
                  container
                  xs={12}
                  css={{
                    marginBottom: theme.spacing(3),
                    columnGap: 20,
                    [theme.breakpoints.down('xs')]: {
                      flexDirection: 'column',
                      flexWrap: 'nowrap',
                      rowGap: 24,
                      textAlign: 'center',
                    },
                  }}
                  data-noprint
                >
                  <Grid item sm={4}>
                    <PGEButton
                      type="button"
                      variant={'outlined'}
                      fullWidth
                      onClick={() =>
                        void navigate(
                          ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_CANCEL.replace(
                            ':jobId',
                            jobId,
                          ),
                        )
                      }
                      css={{
                        color: colors.sparkBlue,
                        borderColor: colors.sparkBlue,
                        fontSize: '18px',
                        '&:hover': {
                          backgroundColor: colors.lightBlue,
                          borderColor: 'unset',
                        },
                        borderRadius: '30px',
                        '.MuiButton-label': {
                          fontWeight: 500,
                        },
                        [theme.breakpoints.down('xs')]: {
                          width: '70%',
                        },
                      }}
                    >
                      {content.get('CANCEL_JOB_BUTTON')}
                    </PGEButton>
                  </Grid>
                  <Grid item sm={3}>
                    <PGEButton
                      type="button"
                      variant={'outlined'}
                      fullWidth
                      css={{
                        color: colors.sparkBlue,
                        borderColor: colors.sparkBlue,
                        fontSize: '18px',
                        borderRadius: '30px',
                        '&:hover': {
                          backgroundColor: colors.lightBlue,
                          borderColor: 'unset',
                        },
                        '.MuiButton-label': {
                          fontWeight: 500,
                        },
                        [theme.breakpoints.down('xs')]: {
                          width: '70%',
                        },
                      }}
                      onClick={() =>
                        void navigate(
                          ROUTES.PGE_PLUS_INSTALLER_PORTAL_REQUOTE.replace(
                            ':jobId',
                            jobId,
                          ),
                        )
                      }
                    >
                      {content.get('REQUOTE_BUTTON')}
                    </PGEButton>
                  </Grid>
                  <Grid
                    item
                    sm={4}
                    css={{
                      alignSelf: 'center',
                    }}
                  >
                    <Typography
                      onClick={(e: React.MouseEvent<Element, MouseEvent>) =>
                        handleDownload(e)
                      }
                      css={{
                        color: colors.sparkBlue,
                        fontWeight: 500,
                        fontSize: '18px !important',
                        cursor: 'pointer',
                      }}
                      role="button"
                    >
                      {content.get('REUSABLE_DOWNLOAD_LEAD_PACKAGE')}{' '}
                      <span
                        css={{
                          verticalAlign: 'middle',
                        }}
                      >
                        <SaveAltIcon />
                      </span>
                    </Typography>
                  </Grid>
                </Grid>
              </>
            ) : (
              <></>
            )}
          </>
        )}
        {cancelReasonInfo?.statusReason && (
          <>
            <Grid item xs={12} css={{ marginBottom: 50 }}>
              <Typography
                variant={'body1'}
                css={{
                  fontSize: '20px !important',
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '16px !important',
                  },
                }}
              >
                {cancelReasonInfo?.reasonTitle}
              </Typography>
              <Typography
                variant={'body1'}
                css={{
                  fontSize: '20px !important',
                  fontWeight: 700,
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '16px !important',
                  },
                }}
              >
                {cancelReasonInfo?.statusReason}
              </Typography>
            </Grid>
            {cancelReasonInfo.hideDownloadOption !== false && (
              <Grid
                item
                xs={12}
                css={{
                  alignSelf: 'center',
                  marginBottom: 30,
                }}
              >
                <Typography
                  onClick={(e: React.MouseEvent<Element, MouseEvent>) =>
                    handleDownload(e)
                  }
                  css={{
                    color: colors.sparkBlue,
                    fontWeight: 500,
                    fontSize: '18px !important',
                    cursor: 'pointer',
                  }}
                  role="button"
                >
                  {cancelReasonInfo?.downloadPackageTitle}{' '}
                  <span
                    css={{
                      verticalAlign: 'middle',
                    }}
                  >
                    <SaveAltIcon />
                  </span>
                </Typography>
              </Grid>
            )}
          </>
        )}
        {pendingReasonInfo?.jobStatus === QuoteStatus.Created &&
          pendingReasonInfo?.successTitle && (
            <>
              <Grid item xs={12} css={{ marginBottom: 30 }}>
                <Box
                  css={{
                    backgroundColor: colors.shoreGreen,
                    color: colors.navBarBackground,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    variant={'body1'}
                    css={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: theme.spacing(1.3, 0),
                      fontSize: '20px !important',
                      fontWeight: 600,
                      [theme.breakpoints.down('sm')]: {
                        fontSize: '16px !important',
                      },
                    }}
                  >
                    <SuccessIcon
                      style={{ fontSize: 20, margin: theme.spacing(0, 2) }}
                    />
                    {pendingReasonInfo?.successTitle}
                  </Typography>
                  <CloseIcon
                    style={{ fontSize: 20, margin: theme.spacing(0, 2) }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} css={{ marginBottom: 30 }}>
                <Button
                  color={'primary'}
                  variant={'outlined'}
                  startIcon={<SaveAltIcon style={{ fontSize: 22 }} />}
                  css={{
                    marginRight: '20px',
                    borderRadius: '30px',
                    borderColor: colors.sparkBlue,
                    border: '2px solid',
                    color: colors.sparkBlue,
                    height: '48px',
                    fontWeight: 600,
                    fontSize: '18px',
                    '&:hover': {
                      color: colors.white,
                    },
                  }}
                >
                  {pendingReasonInfo?.downloadPackageTitle}
                </Button>
              </Grid>
            </>
          )}

        {jobData?.job?.status === JobStatus.ServiceCompleted && (
          <AwaitingPaymentBanner content={content} />
        )}
        {/* <ReimbersementPaidBanner /> */}

        {pageSubTitle && (
          <>
            <Grid
              item
              xs={12}
              css={{ marginBottom: 24 }}
              data-print-exclude-requote
            >
              <Typography
                variant={'body1'}
                css={{
                  fontSize: '20px !important',
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '16px !important',
                  },
                }}
              >
                {pageSubTitle}
              </Typography>
            </Grid>
          </>
        )}
        {downloadPackageTitle && (
          <Grid
            item
            sm={4}
            css={{
              alignSelf: 'center',
            }}
            data-print-exclude-requote
          >
            <Typography
              onClick={(e: React.MouseEvent<Element, MouseEvent>) =>
                handleDownload && handleDownload(e)
              }
              css={{
                color: colors.sparkBlue,
                fontWeight: 500,
                fontSize: '18px !important',
                cursor: 'pointer',
              }}
              role="button"
            >
              {downloadPackageTitle}{' '}
              <span
                css={{
                  verticalAlign: 'middle',
                }}
              >
                <SaveAltIcon />
              </span>
            </Typography>
          </Grid>
        )}
        <Grid
          item
          container
          xs={12}
          css={{
            borderRadius: '5px',
            padding: '23px 25px',
            backgroundColor: '#F1F1F1',
            boxShadow: 'inset 0px 2px 0px #EB7100',
            rowGap: 10,
            marginBottom: 15,
          }}
        >
          <Grid item container>
            <Grid item xs={6}>
              <Typography
                variant={'body1'}
                css={{
                  color: colors.noirBlur,
                  fontSize: '20px !important',
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '16px !important',
                  },
                }}
              >
                {content.get('REUSABLE_REFERENCE_NUMBER_COLON')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant={'body1'}
                css={{
                  color: colors.noirBlur,
                  fontSize: '20px !important',
                  fontWeight: 600,
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '16px !important',
                  },
                }}
              >
                {jobData?.application.applicationNo}
              </Typography>
            </Grid>
          </Grid>
          <Grid item container>
            <Grid item xs={6}>
              <Typography
                variant={'body1'}
                css={{
                  color: colors.noirBlur,
                  fontSize: '20px !important',
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '16px !important',
                  },
                }}
              >
                {content.get('REUSABLE_SERVICE_ADDRESS_LABEL')}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography
                variant={'body1'}
                css={{
                  color: colors.noirBlur,
                  fontWeight: 600,
                  fontSize: '20px !important',
                  [theme.breakpoints.down('sm')]: {
                    fontSize: '16px !important',
                  },
                }}
              >
                {jobData?.job?.jobLocation?.addressLine1}
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Fragment>
  );
};

export default InstallerPageHeader;

const ReimbersementPaidBanner = () => {
  return (
    <Grid
      item
      container
      xs={12}
      css={{
        borderRadius: '5px',
        padding: '25px',
        backgroundColor: colors.lightBlue1,
        rowGap: 10,
        marginBottom: theme.spacing(4),
      }}
    >
      <Grid item container>
        <Grid item>
          <Typography variant={'body1'} css={fontRegular}>
            Your reimbursement has been processed.
          </Typography>
          <Typography css={{ ...fontRegular, marginTop: theme.spacing(2) }}>
            Transaction number: 000000000
          </Typography>
          <Typography css={fontRegular}>Transaction date: 09/17/24</Typography>
        </Grid>
        <Grid
          item
          container
          justify="space-between"
          css={{ display: 'flex', marginTop: theme.spacing(2) }}
        >
          <Grid item>
            <Typography css={fontBold}>Reimbursement amount:</Typography>
          </Grid>
          <Grid item>
            <Typography css={fontBold}>$1500.00</Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

const AwaitingPaymentBanner = ({
  content,
}: {
  content: PageTextContentType;
}) => {
  return (
    <Grid
      item
      container
      xs={12}
      css={{
        borderRadius: '5px',
        padding: '25px',
        backgroundColor: colors.lightBlue1,
        rowGap: 10,
        marginBottom: theme.spacing(4),
      }}
    >
      <Grid item container>
        <Grid item>
          <Typography variant={'body1'} css={fontRegular}>
            {content.get('INSTALLER_AWAITING_REIMBURSEMENT_INFO')}
          </Typography>
        </Grid>
        {/* <Grid
          item
          container
          justify="space-between"
          css={{ display: 'flex', marginTop: theme.spacing(2) }}
        >
          <Grid item>
            <Typography css={fontBold}>Reimbursement amount:</Typography>
          </Grid>
          <Grid item>
            <Typography css={fontBold}>$1500.00</Typography>
          </Grid>
        </Grid> */}
      </Grid>
    </Grid>
  );
};
